/**
 * sl-m82f.4.2: AI Chat feature tab.
 * sl-4wjh.3.2: Extended with context support (user vs workflow).
 * sl-18pc.5: Renamed automation → workflow at the analytics API boundary.
 *
 * Shows per-user or per-workflow usage for capability in (chat, tool).
 * In workflow context, fetches from /ai/analytics/workflows instead.
 */
import { useI18n } from '@affine/i18n';
import { useCallback, useMemo } from 'react';

import { fetchAiChatUsers, fetchAiChatWorkflows } from '../api';
import { FeatureTabShell } from '../feature-tab-shell';
import { formatCount, formatCredits, formatTopModelLabel } from '../format';
import {
  computeTopModel,
  computeTotalCalls,
  type FetchResult,
  toBreakdownRows,
} from '../row-utils';
import type { SummaryCardData } from '../tab-summary-card';
import type { AnalyticsPeriodRange, ContextType } from '../types';
import { useAsyncResource } from '../use-async-resource';

const I18N = 'com.streamlain.settings.workspace.ai.analytics' as const;

export interface AiChatTabProps {
  workspaceId: string;
  period: string | AnalyticsPeriodRange;
  context: ContextType;
}

export const AiChatTab = ({ workspaceId, period, context }: AiChatTabProps) => {
  const t = useI18n();
  const isWorkflow = context === 'workflow';

  const fetcher = useCallback(
    (): Promise<FetchResult> =>
      isWorkflow
        ? fetchAiChatWorkflows(workspaceId, period)
        : fetchAiChatUsers(workspaceId, period),
    [workspaceId, period, isWorkflow]
  );

  const { data, isLoading, error } = useAsyncResource(
    `ai-chat:${context}:${workspaceId}:${typeof period === 'string' ? period : `${period.key}:${period.since}:${period.until}`}`,
    fetcher
  );

  const rows = useMemo(
    () => (data ? toBreakdownRows(data, isWorkflow) : []),
    [data, isWorkflow]
  );

  const topModel = useMemo(() => computeTopModel(rows), [rows]);
  const totalCalls = useMemo(() => computeTotalCalls(rows), [rows]);
  // Sum credits from the context-scoped breakdown rows (not from the
  // context-agnostic featureTotals.aiChat) so the total card matches the
  // list below — mixing workflow-chat into the user-tab's total inflates
  // the number by calls that don't appear in the rows.
  const totalCredits = useMemo(
    () => rows.reduce((sum, row) => sum + row.credits, 0),
    [rows]
  );

  const summary: readonly [SummaryCardData, SummaryCardData, SummaryCardData] =
    [
      {
        label: t[`${I18N}.card.total-credits`]?.() ?? 'Total credits',
        value: formatCredits(totalCredits),
      },
      {
        label: t[`${I18N}.card.call-count`]?.() ?? 'Calls',
        value: formatCount(totalCalls),
        subtext: t[`${I18N}.card.call-count.sub`]?.() ?? 'In selected period',
      },
      {
        label: t[`${I18N}.card.top-model`]?.() ?? 'Top model',
        value: topModel ? formatTopModelLabel(topModel.model) : '-',
        subtext: topModel
          ? `${formatCredits(topModel.credits)} credits \u00B7 ${formatCount(totalCalls)} calls`
          : undefined,
        smallValue: true,
      },
    ] as const;

  return (
    <FeatureTabShell
      summary={summary}
      rows={rows}
      nameHeader={
        isWorkflow
          ? (t[`${I18N}.table.workflow-header`]?.() ?? 'Workflow')
          : (t[`${I18N}.table.user-header`]?.() ?? 'User')
      }
      isLoading={isLoading}
      error={error}
      resetSignal={`${typeof period === 'string' ? period : period.key}:${context}`}
      hideUserFilter={isWorkflow}
    />
  );
};
